import React, {useEffect, useRef} from 'react'
import {Box, Skeleton, useDisclosure} from '@salesforce/retail-react-app/app/components/shared/ui'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {Global} from '@emotion/react'
import PropTypes from 'prop-types'
import useTagManager from '../../hooks/use-tag-manager'
import {useGlobaleContext} from '../../contexts/globale'
import useContentSlot from '../../hooks/use-content-slot'
import HeaderPromoBarDropdown from './partials/header-promo-bar-dropdown'

// Fixes race condition between GlobalE JS execution and our DOM render
const ensureGlobaleHasContent = () => {
    const globaleContainer = document.querySelector('#GlobaleFreeShippingBannerContainer')
    const hasContent = globaleContainer && !!globaleContainer.innerHTML

    if (!hasContent && typeof window.handleFreeShippingBanner === 'function') {
        window.handleFreeShippingBanner()
    }
}

const HeaderPromoBar = ({key, brand}) => {
    const {countryISO, globaleCountry, globaleCurrency} = useGlobaleContext()
    const tagManager = useTagManager()
    const isYR = brand === 'YR'
    const promoBarRef = useRef(null)
    // keep stable refs for timers/intervals to avoid leaks across site switches
    const countdownIntervalRef = useRef(null)
    const timerRetryRef = useRef(null)
    const slidesIntervalRef = useRef(null)
    const slidesRetryRef = useRef(null)

    const {isOpen, onClose, onOpen} = useDisclosure()
    const {
        app: {commerceAPI}
    } = getConfig()

    const {globalE} = commerceAPI || {}
    const {config} = commerceAPI.promotions || {}
    const {siteTheme} = commerceAPI.storefront || {}

    let themeConfig = {}
    let promotionConfig = {}
    try {
        themeConfig = JSON.parse(siteTheme || '{}') || {}
    } catch (e) {
        // Invalid optional styling config should not hide the promotion.
        console.error(e.message)
    }
    try {
        promotionConfig = JSON.parse(config || '{}') || {}
    } catch (e) {
        // Invalid optional styling config should not hide the promotion.
        console.error(e.message)
    }
    const brandTheme = themeConfig[isYR ? 'yr' : 'ks'] || {}
    const configuredPromoBackground =
        brandTheme.promoBarBackground ||
        brandTheme.promoBarBg ||
        promotionConfig[isYR ? 'yrPromoBarBackground' : 'ksPromoBarBackground']
    const promoBackground =
        configuredPromoBackground ||
        (isYR ? 'var(--chakra-colors-black-300)' : 'var(--chakra-colors-yellow-600)')
    const promoTextColor =
        brandTheme.promoBarTextColor ||
        brandTheme.promoBarColor ||
        promotionConfig[isYR ? 'yrPromoBarTextColor' : 'ksPromoBarTextColor'] ||
        (isYR ? 'var(--chakra-colors-white)' : 'var(--chakra-colors-black-300)')

    let enableKsPromoBar = false,
        enableYrPromoBar = false

    let promoId,
        promoDropdownId1,
        promoDropdownId2,
        promoDropdownId3 = ''
    let brandPromoBanner = isYR ? 'yr-promo-banner' : 'ks-promo-banner'
    enableKsPromoBar = promotionConfig.enableKsPromoBar
    enableYrPromoBar = promotionConfig.enableYrPromoBar

    useEffect(() => {
        if (globaleCurrency === 'USD') return

        const interval = setInterval(() => {
            if (window?.GlobalE) {
                window.GlobalE.GetFreeShippingBanner()

                const el = document.querySelector('.globale-box')
                if (el) {
                    el.classList.remove('hide-element')
                }

                clearInterval(interval)
            }
        }, 100)

        return () => clearInterval(interval)
    }, [globaleCurrency])

    if (enableKsPromoBar && brand !== 'YR') {
        promoId = 'header-shipping-banner'
        promoDropdownId1 = 'promo-dropdown-1'
        promoDropdownId2 = 'promo-dropdown-2'
        promoDropdownId3 = 'promo-dropdown-3'
    } else if (enableYrPromoBar && brand === 'YR') {
        promoId = 'header-shipping-banner-yr'
        promoDropdownId1 = 'promo-dropdown-yr-1'
        promoDropdownId2 = 'promo-dropdown-yr-2'
        promoDropdownId3 = 'promo-dropdown-yr-3'
    }

    const defaultCountry = 'US'
    const {data: slotData, isLoading} = useContentSlot(promoId, countryISO || defaultCountry)
    const hasTimer = typeof slotData === 'string' && slotData.includes('promobar-timer-data')

    const {data: promoData1} = useContentSlot(promoDropdownId1, countryISO || defaultCountry)
    const {data: promoData2} = useContentSlot(promoDropdownId2, countryISO || defaultCountry)
    const {data: promoData3} = useContentSlot(promoDropdownId3, countryISO || defaultCountry)
    const promoData = {
        promoData1: promoData1?.trim(),
        promoData2: promoData2?.trim(),
        promoData3: promoData3?.trim()
    }

    useEffect(() => {
        // Clear any previous intervals/timeouts when dependencies change (e.g., site switch)
        if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current)
            countdownIntervalRef.current = null
        }
        if (timerRetryRef.current) {
            clearTimeout(timerRetryRef.current)
            timerRetryRef.current = null
        }
        if (slidesRetryRef.current) {
            clearTimeout(slidesRetryRef.current)
            slidesRetryRef.current = null
        }
        if (slidesIntervalRef.current) {
            clearInterval(slidesIntervalRef.current)
            slidesIntervalRef.current = null
        }

        const updateCountdown = () => {
            const original = document.querySelector('.promobar-timer-data')
            if (!original) return

            const dateAttr = original.getAttribute('data-date')
            if (!dateAttr) return

            const withTimers = document.querySelectorAll('.banner-box.with-timer')

            withTimers.forEach((box, index) => {
                const target = box.querySelector('.banner-box-text')
                if (!target) return

                // FIRST one uses the ORIGINAL
                const timer = index === 0 ? original : original.cloneNode(true)

                // remove existing timer if any
                target.querySelector('.promobar-timer-data')?.remove()

                // now append fresh one
                target.appendChild(timer)

                // countdown calc
                const endDate = new Date(dateAttr)
                const now = new Date()
                const diff = endDate - now

                if (diff <= 0) {
                    timer.textContent = '0 days 0 hrs 0 m 0 s'
                    return
                }

                const seconds = Math.floor((diff / 1000) % 60)
                const minutes = Math.floor((diff / 1000 / 60) % 60)
                const hours = Math.floor((diff / (1000 * 60 * 60)) % 24)
                const days = Math.floor(diff / (1000 * 60 * 60 * 24))
                const dayLabel = days === 1 ? 'day' : 'days'

                timer.textContent = `${days} ${dayLabel} ${hours} hrs ${minutes} m ${seconds} s`
            })
        }

        const startSlides = () => {
            // ensure previous interval is cleared
            if (slidesIntervalRef.current) {
                clearInterval(slidesIntervalRef.current)
                slidesIntervalRef.current = null
            }

            const bannerBoxes = document.querySelectorAll(
                '.promo-banner-wrapper div.banner-container-new .banner-box'
            )
            if (!bannerBoxes || bannerBoxes.length === 0) return

            // set initial active immediately (preserve existing active if any)
            let currentIndex = 0
            bannerBoxes.forEach((el, idx) => {
                if (el.classList.contains('active')) {
                    currentIndex = idx
                }
                el.classList.remove('active')
            })
            bannerBoxes[currentIndex]?.classList.add('active')

            // only auto-slide if more than one banner
            if (bannerBoxes.length > 1) {
                slidesIntervalRef.current = setInterval(() => {
                    const boxes = document.querySelectorAll(
                        '.promo-banner-wrapper  div.banner-container-new .banner-box'
                    )
                    if (!boxes || boxes.length <= 1) return

                    const nextIndex = (currentIndex + 1) % boxes.length
                    boxes[nextIndex]?.classList.add('active')
                    boxes[currentIndex]?.classList.remove('active')
                    currentIndex = nextIndex
                }, 8000)
            }
        }

        const waitForSlides = () => {
            const banners = document.querySelectorAll(
                '.promo-banner-wrapper  div.banner-container-new .banner-box'
            )
            if (banners.length > 0) {
                startSlides()
            } else {
                slidesRetryRef.current = setTimeout(waitForSlides, 250)
            }
        }

        const waitForTimer = () => {
            const container = document.querySelector('.promobar-timer-data')
            const hasDate = container?.getAttribute('data-date')

            if (!container || !hasDate) {
                timerRetryRef.current = setTimeout(waitForTimer, 250)
                return
            }

            // Start interval with dynamic date fetching each second
            countdownIntervalRef.current = setInterval(updateCountdown, 1000)

            // Run once immediately
            updateCountdown()
        }

        if (promoBarRef?.current) {
            waitForTimer()
            waitForSlides()
        }

        return () => {
            if (countdownIntervalRef.current) {
                clearInterval(countdownIntervalRef.current)
                countdownIntervalRef.current = null
            }
            if (timerRetryRef.current) {
                clearTimeout(timerRetryRef.current)
                timerRetryRef.current = null
            }
            if (slidesRetryRef.current) {
                clearTimeout(slidesRetryRef.current)
                slidesRetryRef.current = null
            }
            if (slidesIntervalRef.current) {
                clearInterval(slidesIntervalRef.current)
                slidesIntervalRef.current = null
            }
        }
    }, [promoBarRef, slotData, brand, countryISO])

    useEffect(() => {
        if (!promoBarRef.current) return

        // Find element inside the dangerouslySetInnerHTML content
        const triggerEl = promoBarRef.current.querySelector('.banner-container-new')
        const allOffersEl = promoBarRef.current.querySelector('.all-offers-arrow')
        const hasPopover = promoData.promoData1 || promoData.promoData2 || promoData.promoData3

        const handleClick = (e) => {
            // trigger only if not clicking on a link
            if (e.target.closest('a')) return
            onOpen()
        }

        if (triggerEl && allOffersEl && hasPopover) {
            triggerEl.addEventListener('click', handleClick)
            return () => triggerEl.removeEventListener('click', handleClick)
        } else {
            allOffersEl?.remove()
        }
    }, [promoBarRef?.current, brand])

    const promotionClick = (e) => {
        const promo = e?.target?.closest('.banner-box')?.querySelector('.show-all-offers')
        const name = promo?.getAttribute('data-promo-name')
        const id = promo?.getAttribute('data-promo-id')
        if (!name || !id) {
            return
        }
        tagManager.promotionClick({
            id: id,
            name: name,
            creative: undefined,
            position: isYR ? 'header-shipping-banner-yr' : 'header-shipping-banner'
        })
    }

    if ((!enableKsPromoBar && !isYR) || (!enableYrPromoBar && isYR)) {
        return
    }

    if (!!globaleCountry && globaleCountry !== 'US') {
        ensureGlobaleHasContent()

        return globalE?.promoBarMessage ? (
            <Box
                className="gloable-promo-wrapper promo-banner-wrapper"
                sx={{
                    '.ge-free-shipping-msg': {
                        padding: '5px 0'
                    },
                    '#GlobaleFreeShippingBannerContainer, div[data-promo-name="globale-promo"]': {
                        margin: 'auto',
                        color: `${promoTextColor} !important`
                    },
                    '#GlobaleFreeShippingBannerContainer *, div[data-promo-name="globale-promo"] *':
                        {
                            color: `${promoTextColor} !important`
                        },
                    '#GlobaleFreeShippingBannerContainer > div:not(:last-child)': {
                        display: 'none' // make sure we'll not display duplicate promo messages
                    }
                }}
                data-country={countryISO}
                display="block"
                minHeight="40px"
                cursor="pointer"
                fontFamily="heading"
                fontSize="16px"
                fontWeight="300"
                lineHeight="20px"
                alignContent="center"
                letterSpacing="0.08px"
                borderBottom={isYR ? '0.25px solid' : '0.5px solid'}
                borderColor={isYR ? 'gray.700' : 'gray.400'}
                textAlign="center"
                background={promoBackground}
                color={promoTextColor}
                dangerouslySetInnerHTML={{__html: globalE?.promoBarMessage}}
            />
        ) : null
    }

    return !countryISO || countryISO === 'US' ? (
        <>
            {isLoading && (
                <Skeleton
                    key={key}
                    height={{base: 'auto', lg: '40px'}}
                    maxHeight={{base: '80px', lg: '40px'}}
                    minHeight="40px"
                    width="100%"
                    startColor="gray.200"
                    opacity="1 !important"
                    backgroundColor={`${promoBackground} !important`}
                    endColor="gray.200"
                    borderTop="0.5px solid #EDE8E3"
                />
            )}

            {!isLoading && typeof slotData === 'string' && slotData?.trim() && (
                <Box
                    ref={promoBarRef}
                    key={brand + '-promobar'}
                    className={`promo-banner-wrapper ${brandPromoBanner}`}
                    background={promoBackground}
                    color={promoTextColor}
                    onClick={promotionClick}
                    display="flex"
                    overflow="hidden"
                    height={{base: hasTimer ? '60px' : '40px', lg: '40px'}}
                    maxHeight={{base: hasTimer ? '60px' : '40px', lg: '40px'}}
                    minHeight={{base: hasTimer ? '60px' : '40px', lg: '40px'}}
                    px={{base: '15px', lg: '60px'}}
                    py={{base: '10px', lg: '0'}}
                    justifyContent="center"
                    sx={{
                        '.banner-box-text': {
                            textAlign: {lg: 'center'}
                        },
                        '.banner-box, .banner-box a, .promobar-timer-data': {
                            color: `${promoTextColor} !important`
                        },
                        '.all-offers-arrow': {
                            filter: isYR ? 'invert(1)' : 'none'
                        }
                    }}
                    alignItems="center"
                    alignSelf="stretch"
                    position="static"
                    borderBottom={isYR ? '0.25px solid' : '0.5px solid'}
                    borderColor={isYR ? 'gray.700' : 'gray.400'}
                    top="unset"
                    zIndex="2"
                >
                    <Global
                        styles={`
                            @media (max-width: 768px) {
                                .header-shipping-banner p {
                                    font-size: 14px;
                                    min-width: 0;
                                    text-align: center;
                                }

                                .banner-box-text {
                                    padding: 0 15px;
                                    justify-content: center;
                                    min-width: 0;
                                }

                                .show-all-offers {
                                    text-align: center;
                                }

                            }
                            @media (max-width: 61.999em) {
                                .header-shipping-banner .banner-box.with-timer .banner-box-text {
                                    justify-content: flex-start;
                                    text-align: left;
                                }

                                .header-shipping-banner .banner-box.with-timer p {
                                    -webkit-line-clamp: 2;
                                    line-clamp: 2;
                                    text-align: left;
                                }
                            }
                            @media (min-width: 769px) and (max-width: 1024px) {
                                .header-shipping-banner {
                                    justify-content: flex-end;
                                    display: flex;
                                }

                                .banner-box-text {
                                    padding: 0 15px;
                                }

                            }
                        `}
                    />

                    <div dangerouslySetInnerHTML={{__html: slotData}} />

                    {isOpen && promoBarRef.current && (
                        <HeaderPromoBarDropdown
                            key={brand + 'promobarDropdown'}
                            promoData={promoData}
                            ref={promoBarRef.current}
                            onClose={onClose}
                            brand={brand}
                            hasTimer={hasTimer}
                        />
                    )}
                </Box>
            )}
        </>
    ) : null
}

HeaderPromoBar.propTypes = {
    key: PropTypes.string,
    brand: PropTypes.string
}
export default HeaderPromoBar
